import { useMemo, useState, type ReactNode } from "react";
import { CreditCard, MessageCircle, Repeat, User, Zap } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type SourceFilter = "whatsapp" | "cc-sync" | "recurring" | "web" | "automation";

interface CategoryChip {
  id: string;
  label: string;
  color: string;
}

interface FilterChipsProps {
  title: string;
  allLabel: string;
  seeAllLabel: string;
  categories: CategoryChip[];
  activeCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  sourceLabels: Record<SourceFilter, string>;
  activeSource: SourceFilter | null;
  onSourceChange: (source: SourceFilter | null) => void;
}

const sourceIcons: Record<SourceFilter, React.ComponentType<{ size?: number; className?: string }>> = {
  whatsapp: MessageCircle,
  "cc-sync": CreditCard,
  recurring: Repeat,
  web: User,
  automation: Zap,
};

const MAX_VISIBLE_CATEGORIES = 4;
const MAX_VISIBLE_SOURCES = 4;

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 rounded-[100px] border px-[9px] py-[4px] text-[11px] font-normal text-[var(--text-secondary)] transition-all [transition-duration:150ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
        active
          ? "border-[var(--fg-accent)] bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]"
          : "border-[var(--border-color)] hover:border-[var(--fg-accent)] hover:text-[var(--text-primary)]",
      )}
    >
      {children}
    </button>
  );
}

export default function FilterChips({
  title,
  allLabel,
  seeAllLabel,
  categories,
  activeCategoryId,
  onCategoryChange,
  sourceLabels,
  activeSource,
  onSourceChange,
}: FilterChipsProps) {
  const [showAllOpen, setShowAllOpen] = useState(false);
  const [showAllSourcesOpen, setShowAllSourcesOpen] = useState(false);
  const visibleCategories = useMemo(() => categories.slice(0, MAX_VISIBLE_CATEGORIES), [categories]);
  const hiddenCategories = useMemo(() => categories.slice(MAX_VISIBLE_CATEGORIES), [categories]);
  const sourceEntries = useMemo(() => Object.keys(sourceLabels) as SourceFilter[], [sourceLabels]);
  const visibleSources = useMemo(() => sourceEntries.slice(0, MAX_VISIBLE_SOURCES), [sourceEntries]);
  const hiddenSources = useMemo(() => sourceEntries.slice(MAX_VISIBLE_SOURCES), [sourceEntries]);

  return (
    <div className="space-y-1.5 px-1">
      <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--text-muted)]">{title}:</span>
        <Chip active={!activeCategoryId} onClick={() => onCategoryChange(null)}>
          <span className="max-w-[96px] truncate">{allLabel}</span>
        </Chip>
        {visibleCategories.map((category) => (
          <Chip
            key={category.id}
            active={activeCategoryId === category.id}
            onClick={() => onCategoryChange(activeCategoryId === category.id ? null : category.id)}
          >
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
            <span className="max-w-[84px] truncate">{category.label}</span>
          </Chip>
        ))}
        {hiddenCategories.length > 0 ? (
          <Popover open={showAllOpen} onOpenChange={setShowAllOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-[100px] border border-[var(--border-color)] px-[9px] py-[4px] text-[11px] font-normal text-[var(--text-secondary)] transition-all hover:border-[var(--fg-accent)] hover:text-[var(--text-primary)]"
              >
                +{hiddenCategories.length} {seeAllLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 border-[var(--border-color)] bg-[var(--bg-secondary)] p-2">
              <div className="max-h-56 space-y-1 overflow-y-auto">
                {hiddenCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] font-normal text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]",
                      activeCategoryId === category.id && "bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]",
                    )}
                    onClick={() => {
                      onCategoryChange(activeCategoryId === category.id ? null : category.id);
                      setShowAllOpen(false);
                    }}
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
                    <span className="truncate">{category.label}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
        <span className="w-[56px] shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-[transparent]">
          {title}
        </span>
        {visibleSources.map((source) => {
          const Icon = sourceIcons[source];
          return (
            <Chip
              key={source}
              active={activeSource === source}
              onClick={() => onSourceChange(activeSource === source ? null : source)}
            >
              <Icon size={11} />
              <span className="max-w-[76px] truncate">{sourceLabels[source]}</span>
            </Chip>
          );
        })}
        {hiddenSources.length > 0 ? (
          <Popover open={showAllSourcesOpen} onOpenChange={setShowAllSourcesOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-[100px] border border-[var(--border-color)] px-[9px] py-[4px] text-[11px] font-normal text-[var(--text-secondary)] transition-all hover:border-[var(--fg-accent)] hover:text-[var(--text-primary)]"
              >
                +{hiddenSources.length}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 border-[var(--border-color)] bg-[var(--bg-secondary)] p-2">
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {hiddenSources.map((source) => {
                  const Icon = sourceIcons[source];
                  return (
                    <button
                      key={source}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] font-normal text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]",
                        activeSource === source && "bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]",
                      )}
                      onClick={() => {
                        onSourceChange(activeSource === source ? null : source);
                        setShowAllSourcesOpen(false);
                      }}
                    >
                      <Icon size={11} />
                      <span className="truncate">{sourceLabels[source]}</span>
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </div>
  );
}
